import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  getRmaList,
  getRma,
  createRma,
  updateRma,
  updateRmaStatus,
  deleteRma,
  generateRmaLabel,
  getRmaRates,
  addRmaItem,
  removeRmaItem,
  updateRmaItem,
  getRmaQuoteContext,
  createRmaFromQuote,
} from '../api';
import { rmaKeys } from './queryKeys';
import type {
  RmaFilters,
  CreateRmaInput,
  AddRmaItemInput,
  UpdateRmaItemInput,
  GenerateLabelInput,
  RmaStatus,
  CreateRmaFromQuoteInput,
} from '../types';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return fallback;
}

export function useRmaList(filters: RmaFilters = {}) {
  return useQuery({
    queryKey: rmaKeys.list(filters),
    queryFn: () => getRmaList(filters),
  });
}

export function useRma(id: string) {
  return useQuery({
    queryKey: rmaKeys.detail(id),
    queryFn: () => getRma(id),
    enabled: !!id,
  });
}

export function useCreateRma() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: (data: CreateRmaInput) => createRma(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.lists() });
      toast.success(t('rma.toast.createSuccess', 'RMA created successfully'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.createFailed', 'Failed to create RMA'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

export function useUpdateRma() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateRmaInput> }) => updateRma(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: rmaKeys.lists() });
    },
    onError: (error) => {
      toast.error(t('rma.toast.updateFailed', 'Failed to update RMA'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

export function useUpdateRmaStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: RmaStatus }) => updateRmaStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: rmaKeys.lists() });
      toast.success(t('rma.toast.statusUpdated', 'Status updated'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.statusFailed', 'Failed to update status'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

export function useDeleteRma() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: (id: string) => deleteRma(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.lists() });
      toast.success(t('rma.toast.deleted', 'RMA deleted'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.deleteFailed', 'Failed to delete RMA'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

export function useGenerateRmaLabel() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: GenerateLabelInput }) => generateRmaLabel(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: rmaKeys.lists() });
      toast.success(t('rma.toast.labelGenerated', 'Return label generated'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.labelFailed', 'Failed to generate label'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

/**
 * On-demand carrier comparison (Canada Post vs Planète Courrier) for a draft RMA.
 * No toast on error — RmaShippingCard renders the failure inline under the
 * Compare button so it STAYS visible (a ~4s toast left a failed compare
 * looking like a dead button, found live 2026-10-09).
 */
export function useRmaRates() {
  return useMutation({
    mutationFn: ({ id, weight }: { id: string; weight?: number }) => getRmaRates(id, weight),
  });
}

export function useAddRmaItem() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: ({ rmaId, data }: { rmaId: string; data: AddRmaItemInput }) => addRmaItem(rmaId, data),
    onSuccess: (_, { rmaId }) => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.detail(rmaId) });
      toast.success(t('rma.toast.itemAdded', 'Item added'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.itemAddFailed', 'Failed to add item'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

export function useRemoveRmaItem() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: ({ rmaId, itemId }: { rmaId: string; itemId: string }) => removeRmaItem(rmaId, itemId),
    onSuccess: (_, { rmaId }) => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.detail(rmaId) });
      toast.success(t('rma.toast.itemRemoved', 'Item removed'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.itemRemoveFailed', 'Failed to remove item'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

export function useUpdateRmaItem() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('operations');
  return useMutation({
    mutationFn: ({ rmaId, itemId, data }: { rmaId: string; itemId: string; data: UpdateRmaItemInput }) =>
      updateRmaItem(rmaId, itemId, data),
    onSuccess: (_, { rmaId }) => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.detail(rmaId) });
      toast.success(t('rma.toast.itemUpdated', 'Item updated'));
    },
    onError: (error) => {
      toast.error(t('rma.toast.itemUpdateFailed', 'Failed to update item'), {
        description: getErrorMessage(error, t('rma.toast.unexpectedError', 'An unexpected error occurred')),
      });
    },
  });
}

// --- Quote -> RMA (auto-populate) -------------------------------------------

export function useRmaQuoteContext(quoteId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: rmaKeys.quoteContext(quoteId ?? ''),
    queryFn: () => getRmaQuoteContext(quoteId as string),
    enabled: !!quoteId && enabled,
  });
}

/**
 * Atomically create an RMA from a quote. No toast here — the caller owns the
 * messaging, because the shipping-label flow treats a failure as non-blocking
 * (the outbound shipment is already confirmed) and shows its own inline error.
 */
export function useCreateRmaFromQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRmaFromQuoteInput) => createRmaFromQuote(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rmaKeys.lists() });
    },
  });
}
